import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ImageOff, Info } from "lucide-react";

/**
 * ShowcaseSquare
 * A 1:1 placeholder/photo tile that:
 *  - has a "genie"-style hover (springy rise, slight skew, soft glow)
 *  - opens a fullscreen lightbox on click (shared layout animation)
 *
 * Props:
 *  - id: unique string used as layoutId for shared transitions
 *  - image: optional image URL. When omitted, a labeled placeholder is rendered.
 *  - label: caption shown on the tile (e.g. "IG · 01")
 *  - testid: data-testid for the tile
 */
function ShowcaseSquare({ id, image, label, testid, onOpen, aspectClass }) {
  return (
    <motion.button
      type="button"
      onClick={() => onOpen(id)}
      data-testid={testid}
      aria-label={`Open ${label}`}
      layoutId={`showcase-${id}`}
      whileHover={{
        y: -6,
        scale: 1.06,
        skewY: -1.5,
        rotate: 0.4,
        transition: { type: "spring", stiffness: 280, damping: 14 },
      }}
      whileTap={{ scale: 0.97, skewY: 0, rotate: 0, y: 0 }}
      style={{ transformOrigin: "bottom center" }}
      className={`group/sc relative ${aspectClass} w-full rounded-md border border-white/[0.08] bg-[#0A0B10] overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40`}
    >
      {/* Soft glow that grows on hover (the 'genie' suction lift) */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-1 rounded-lg opacity-0 group-hover/sc:opacity-100 transition-opacity duration-500"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 100%, rgba(192,157,100,0.35) 0%, rgba(192,157,100,0) 70%)",
          filter: "blur(8px)",
        }}
      />

      {image ? (
        <img
          src={image}
          alt={label}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover/sc:scale-[1.08] transition-transform duration-700 ease-out"
        />
      ) : (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] via-transparent to-white/[0.02]" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-zinc-500 group-hover/sc:text-zinc-300 transition-colors">
            <ImageOff className="w-4 h-4 opacity-60" />
            <span className="text-[9px] uppercase tracking-[0.22em]">
              {label}
            </span>
          </div>
        </>
      )}

      {/* Top-right dot indicator */}
      <span
        aria-hidden
        className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-white/30 group-hover/sc:bg-[#C09D64] transition-colors"
      />

      {/* Bottom caption that fades in on hover */}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 px-2 py-1.5 text-[9px] uppercase tracking-[0.22em] text-zinc-200 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover/sc:opacity-100 transition-opacity duration-300">
        {label}
      </span>
    </motion.button>
  );
}

/**
 * Lightbox
 * Fullscreen image viewer using framer-motion shared layoutId for the smooth
 * "genie expand" feel. Closes on backdrop click, close button, or Escape.
 */
function Lightbox({ open, item, onClose, aspectClass }) {
  return (
    <AnimatePresence>
      {open && item && (
        <motion.div
          key="lightbox"
          data-testid="showcase-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={onClose}
        >
          {/* Backdrop */}
          <motion.div
            aria-hidden
            className="absolute inset-0 bg-black/85 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            data-testid="showcase-lightbox-close"
            aria-label="Close"
            className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full border border-white/15 bg-black/40 text-white inline-flex items-center justify-center hover:bg-white hover:text-black transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Expanded tile */}
          <motion.div
            layoutId={`showcase-${item.id}`}
            onClick={(e) => e.stopPropagation()}
            className={`relative h-[min(86vh,820px)] ${aspectClass} rounded-2xl overflow-hidden border border-white/10 bg-[#0A0B10] shadow-[0_40px_120px_-20px_rgba(192,157,100,0.25)]`}
          >
            {item.image ? (
              <img
                src={item.image}
                alt={item.label}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <>
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] via-transparent to-white/[0.02]" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-400 px-8 text-center">
                  <ImageOff className="w-8 h-8 opacity-50" />
                  <p className="font-serif text-2xl md:text-3xl text-white">
                    {item.label}
                  </p>
                  <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 max-w-sm">
                    Soon — this slot is reserved for an Instagram post
                    showcasing work from {item.parentRole}.
                  </p>
                </div>
              </>
            )}

            {/* Bottom caption */}
            <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 flex items-end justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-300 bg-gradient-to-t from-black/80 to-transparent">
              <span>{item.label}</span>
              <span className="text-zinc-500">{item.parentRole}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * ShowcaseGrid
 * Renders a 4-up 1:1 grid of ShowcaseSquares and owns the Lightbox state.
 */
export default function ShowcaseGrid({ parentTestId, parentRole, items, aspect = "square" }) {
  const [openId, setOpenId] = useState(null);
  const openItem = items.find((it) => it.id === openId) || null;

  const aspectClass =
    aspect === "portrait" ? "aspect-[4/5]" : "aspect-square";

  // Choose column count so all items fit in a single row up to 5
  const cols = Math.min(Math.max(items.length, 1), 5);
  const colsClass =
    {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
      5: "grid-cols-5",
    }[cols] || "grid-cols-4";
  // Bumped widths so tiles feel substantial and roughly match a 4:5 portrait
  // tile in physical size on screen. 1:1 rows need a wider container per tile
  // to read close in height to a 4:5 row.
  const widthClass =
    cols >= 5
      ? "max-w-6xl"
      : cols === 4
      ? "max-w-4xl"
      : "max-w-xl";

  // Close on Escape + lock body scroll while open
  useEffect(() => {
    if (!openId) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenId(null);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [openId]);

  return (
    <>
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          <Info className="w-3 h-3 text-[#C09D64]" aria-hidden />
          <span data-testid={`${parentTestId}-hint`}>
            Example works · tap a tile to expand
          </span>
        </div>
        <div
          data-testid={parentTestId}
          className={`grid ${colsClass} gap-2 md:gap-3 ${widthClass}`}
        >
        {items.map((it, idx) => (
          <ShowcaseSquare
            key={it.id}
            id={it.id}
            image={it.image}
            label={it.label}
            testid={`${parentTestId}-${idx}`}
            onOpen={setOpenId}
            aspectClass={aspectClass}
          />
        ))}
        </div>
      </div>
      <Lightbox
        open={!!openItem}
        item={openItem ? { ...openItem, parentRole } : null}
        onClose={() => setOpenId(null)}
        aspectClass={aspectClass}
      />
    </>
  );
}
